import React, { useContext, useMemo, useState, useEffect } from 'react';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { css } from '@emotion/react';
import { SectionSubtitle } from '@/styles/blocks';
import StartTimeSelect from '@/components/misc/StartTimeSelect';
import { useSwappedAttributes } from '@/components/ShipmentForm/hooks';
import { Body1, Incrementor, OnwardCheckbox, TabSection, TabTitle, TextField } from '@/components/ShipmentForm/blocks';
import { ModalContext } from '../../context';
import { SectionSubtitle2, GreyBody } from '@/styles/blocks';
import { useClientUser, useOrderNotes } from '@/hooks';
import { capitalize } from 'lodash';
import { UserContext } from '@/components/App';
import { greys } from '@/styles/colors';
import { colors } from '@/styles';
import { PrimaryButton } from '@/styles/blocks';
import { MenuItem, Typography, Grid } from '@material-ui/core';
import { HR } from '@/components/OrderDetailsPage/Notes/Notes';
import { X } from 'react-bootstrap-icons';

const dateShort = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

const timestamp = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
});

const Note = (note, isLast, accountTypeCapital, username, email) => {
    const source_user_type = note?.source_user_type;

    return (
        <>
            <Grid container direction="row">
                <Grid container item direction="column" xs={4}>
                    {note?.private_to === accountTypeCapital && (
                        <Grid item>
                            <Typography
                                css={css`
                                    color: orange;
                                    font-weight: 500;
                                `}
                            >
                                Private
                            </Typography>
                        </Grid>
                    )}

                    {note?.requires_acknowledgement && (
                        <Grid item>
                            <Typography
                                css={css`
                                    color: ${colors.greens.primary};
                                    font-weight: 500;
                                `}
                            >
                                Onward/Shipper Notified
                            </Typography>
                        </Grid>
                    )}

                    <Grid item>
                        <Typography
                            css={css`
                                font-weight: 500;
                                line-break: anywhere;
                            `}
                        >
                            {source_user_type
                                ? source_user_type === 'Admin'
                                    ? 'Onward'
                                    : `(${source_user_type})`
                                : 'System'}
                        </Typography>
                    </Grid>

                    {source_user_type === 'Admin' && note?.user?.username && (
                        <Grid item>
                            <Typography
                                css={css`
                                    font-weight: 500;
                                    line-break: anywhere;
                                `}
                            >
                                {`(${note?.user?.username})`}
                            </Typography>
                        </Grid>
                    )}

                    {note.source_user_type !== 'Admin' && (
                        <Grid item>
                            <Typography
                                css={css`
                                    color: ${greys.primary};
                                    font-weight: 500;
                                    line-break: anywhere;
                                `}
                            >
                                {email}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <Typography
                            css={css`
                                color: ${greys.primary};
                                font-weight: 500;
                            `}
                        >
                            {dateShort.format(new Date(note.created_at || Date.now()))}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography
                            css={css`
                                color: ${greys.primary};
                                font-weight: 500;
                            `}
                        >
                            {timestamp.format(new Date(note.created_at || Date.now()))}
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container direction="column" item xs={8}>
                    {note?.type && (
                        <Grid item>
                            <Typography
                                css={css`
                                    font-weight: 500;
                                `}
                            >
                                {note?.type}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <Typography
                            css={css`
                                font-weight: 500;
                                ${note?.is_acknowledgement &&
                                `
                                    font-style: italic;
                                `}
                            `}
                        >
                            {note?.is_acknowledgement ? `Acknowledged by ${note?.source_user_type}` : note.note}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            {!isLast && <HR />}
        </>
    );
};

const initNote = {
    private: false,
    notify: false,
    note: '',
    type: null,
};

/**
 * Admin Notes section of the Edit Order modal: lists the order's notes (optionally only
 * admin-private ones) and lets the user add a note. Takes no props; reads `ModalContext`.
 */
const AdminNotes = () => {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const { user_id, userType, client_id, isOnwardAdmin } = useClientUser();
    const { order, noteInfo, options } = modalState;
    const { setNoteInfo } = callbacks;
    const { username, email, user } = useContext(UserContext);

    const [collapsed, setCollapsed] = useState(false);
    const [viewAdminNotes, setViewAdminNotes] = useState(false);

    // Resolve the corp admin's role for this specific order so note visibility
    // and authorship reflect the actual relationship (shipper-org vs carrier-org).
    // Falls back to null when no match, which hides all private notes.
    const resolvedUserType = useMemo(() => {
        if (isOnwardAdmin) return 'Admin';
        const shipperIds = options.adminTabShipperIds || [];
        const carrierIds = options.adminTabCarrierIds || [];
        if (shipperIds.includes(order.shipper_id)) return 'Shipper';
        if (
            carrierIds.includes(order.carrier_id) ||
            carrierIds.includes(order.middle_mile_carrier_id) ||
            carrierIds.includes(order.warehouse_carrier_id)
        )
            return 'Carrier';
        // Non-corp-admin viewers have a client_id but no org-wide id lists, so fall back to direct match.
        if (client_id === order.shipper_id) return 'Shipper';
        if (
            client_id === order.carrier_id ||
            client_id === order.middle_mile_carrier_id ||
            client_id === order.warehouse_carrier_id
        )
            return 'Carrier';
        return null;
    }, [
        isOnwardAdmin,
        client_id,
        options.adminTabShipperIds,
        options.adminTabCarrierIds,
        order.shipper_id,
        order.carrier_id,
        order.middle_mile_carrier_id,
        order.warehouse_carrier_id,
    ]);

    const handleAddNote = () => {
        callbacks.addNote({
            note: noteInfo.note,
            source_user_type: resolvedUserType || 'Shipper',
            source_user_id: user.user_id,
            is_acknowledgement: false,
            requires_acknowledgement: false,
            acknowledged_by_shipper: false,
            acknowledged_by_admin: false,
            private_to: isOnwardAdmin ? 'Admin' : null,
            type: noteInfo.type || null,
            order_id: order.order_id,
        });
        setNoteInfo({
            ...initNote,
        });
    };

    const orderNotes = useOrderNotes([order], resolvedUserType);

    const notesForViewing = useMemo(() => {
        if (!viewAdminNotes)
            return (orderNotes?.all || []).sort(
                (a, b) => new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now())
            );

        return (orderNotes?.all || [])
            .filter((note) => note.private_to === 'Admin')
            .sort((a, b) => new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now()));
    }, [orderNotes, viewAdminNotes, order]);

    return (
        <Grid container direction="column">
            <Grid item>
                <TabTitle collapsed={collapsed} callbacks={{ setCollapsed }}>
                    Admin Notes
                </TabTitle>
            </Grid>
            {collapsed ? null : (
                <>
                    {isOnwardAdmin && !!orderNotes?.all?.length && (
                        <Grid
                            item
                            css={css`
                                margin-top: 1rem;
                                margin-bottom: 1rem;
                            `}
                        >
                            <OnwardCheckbox
                                label="View only Admin notes"
                                checked={viewAdminNotes}
                                onChange={() => setViewAdminNotes(!viewAdminNotes)}
                                labelPlacement="end"
                            />
                        </Grid>
                    )}

                    {!notesForViewing?.length ? (
                        <Grid item>
                            <Typography>No {viewAdminNotes ? 'admin ' : ''}notes</Typography>
                        </Grid>
                    ) : (
                        notesForViewing.map((note, idx, arr) =>
                            Note(note, arr.length - 1 === idx, capitalize(resolvedUserType), username, email)
                        )
                    )}
                    <Grid item className="pt-3">
                        <TabSection>Add Note</TabSection>
                    </Grid>
                    <Grid item>
                        <TextField
                            select
                            variant="outlined"
                            label="Type"
                            size="small"
                            value={noteInfo.type || ''}
                            css={css`
                                width: 50%;
                                margin-bottom: 20px;
                                font-weight: 500;
                            `}
                            InputProps={{
                                style: {
                                    fontWeight: 500,
                                },
                            }}
                            InputLabelProps={{
                                style: {
                                    fontWeight: 500,
                                },
                            }}
                            onChange={(e) => {
                                setNoteInfo((prevState) => ({
                                    ...prevState,
                                    type: e.target.value,
                                }));
                            }}
                        >
                            <MenuItem
                                value=""
                                css={css`
                                    font-weight: 500;
                                `}
                            >
                                N/A
                            </MenuItem>
                            <MenuItem
                                value="Invoicing"
                                css={css`
                                    font-weight: 500;
                                `}
                            >
                                Invoicing
                            </MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item>
                        <TextField
                            variant="outlined"
                            name="desc"
                            fullWidth
                            value={noteInfo.note || ''}
                            onChange={(e) =>
                                setNoteInfo((prevState) => ({
                                    ...prevState,
                                    note: e.target.value,
                                }))
                            }
                            multiline
                            rows={5}
                            css={css`
                                textarea {
                                    font-weight: 500;
                                }
                            `}
                        />
                    </Grid>

                    <Grid
                        item
                        css={css`
                            margin-top: 20px;
                        `}
                    >
                        <PrimaryButton disabled={!noteInfo.note} onClick={handleAddNote}>
                            Add Note
                        </PrimaryButton>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default AdminNotes;
